import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";

/**
 * A custom video player strictly for playing raw `.m3u8` or `.mp4` URLs as ads.
 * This completely bypasses VAST/XML requirements and simply plays the video.
 * Skip functionality is handled natively by the ad URL — no manual skip button.
 */
export default function NativeAdPlayer({ adUrl, onComplete }) {
    const videoRef = useRef(null);
    const [hasStarted, setHasStarted] = useState(false);
    const [isError, setIsError] = useState(false);

    useEffect(() => {
        const video = videoRef.current;
        if (!video || !adUrl) return;

        let hls;
        let timeoutId;

        const tryPlay = () => {
            clearTimeout(timeoutId);
            video.play()
                .then(() => setHasStarted(true))
                .catch((err) => {
                    console.warn("NativeAdPlayer: Autoplay blocked or failed", err);
                    setHasStarted(false);
                });
        };

        if (adUrl.includes(".m3u8") && Hls.isSupported()) {
            hls = new Hls({ enableWorker: true });
            hls.loadSource(adUrl);
            hls.attachMedia(video);
            hls.on(Hls.Events.MANIFEST_PARSED, tryPlay);
            hls.on(Hls.Events.ERROR, (event, data) => {
                if (data.fatal) {
                    console.warn("NativeAdPlayer HLS Error:", data);
                    setIsError(true);
                    onComplete(); // Skip ad on fatal error
                }
            });
        } else {
            video.src = adUrl;
            video.addEventListener("loadedmetadata", tryPlay, { once: true });
        }

        const onError = () => {
            setIsError(true);
            onComplete(); // Skip ad if it breaks
        };

        timeoutId = setTimeout(() => {
            console.warn("NativeAdPlayer: Ad connection timeout. Skipping.");
            setIsError(true);
            onComplete();
        }, 8000);

        video.addEventListener("ended", onComplete);
        video.addEventListener("error", onError);

        return () => {
            clearTimeout(timeoutId);
            if (hls) hls.destroy();
            video.removeEventListener("ended", onComplete);
            video.removeEventListener("error", onError);
        };
    }, [adUrl, onComplete]); // Removed canSkip — was causing effect to re-run and reset hasStarted

    return (
        <div className="absolute inset-0 z-50 bg-black flex flex-col justify-center items-center">
            <video
                ref={videoRef}
                playsInline
                className="w-full h-full object-contain"
                autoPlay
                muted={false}
            />

            {/* Ad Label */}
            <div className="absolute top-6 left-6 z-50 bg-black/80 px-3 py-1.5 rounded-lg border border-white/10 shadow-lg">
                <span className="text-yellow-400 font-bold uppercase text-xs tracking-wider">
                    Advertisement
                </span>
            </div>

            {/* Manual Start Fallback — only shown once if autoplay is blocked */}
            {!hasStarted && !isError && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/60 z-40">
                    <button
                        onClick={() =>
                            videoRef.current
                                ?.play()
                                .then(() => setHasStarted(true))
                                .catch(() => { setIsError(true); onComplete(); })
                        }
                        className="w-20 h-20 bg-primary rounded-full flex items-center justify-center shadow-lg hover:scale-105 transition-transform"
                    >
                        <svg className="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                        </svg>
                    </button>
                </div>
            )}
        </div>
    );
}
